import styled from 'styled-components';
import React, { useEffect, useState, useMemo } from "react";
import Empty from "../components/EmptyFeedback";
import { calculate_outcome_summary_chart } from '../factor/calculations';

const CorrelationAnalytics = ({
  core_data,
  outcomeQ,
  data,
  lastData,
  outcomeStandards,
  navigation,
  rule,
}) => {
  const [isEmpty, setIsEmpty] = useState(false);
  const [correlationMatrix, setCorrelationMatrix] = useState(null);

  // Calculate Pearson correlation coefficient
  const calculateCorrelation = (x, y) => {
    const n = x.length;
    if (n === 0) return 0;
    
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((total, xi, i) => total + xi * y[i], 0);
    const sumX2 = x.reduce((total, xi) => total + xi * xi, 0);
    const sumY2 = y.reduce((total, yi) => total + yi * yi, 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    return denominator === 0 ? 0 : numerator / denominator;
  };

  // Text normalizer function from calculations.js
  function normalise(str) {
    if (typeof str !== 'string') return '';
    return str
      .toLowerCase()
      .replace(/\{.*?}/g, '')      // strip {entity} placeholder
      .replace(/\u2026/g, '...')   // … → ...
      .replace(/[.\s]+$/, '')      // trailing dots / spaces
      .replace(/\s+/g, ' ')        // collapse whitespace
      .trim();
  }

  // Check if a response is a valid numeric value (not N/A)
  const isValidResponse = (response) => {
    if (response === null || response === undefined) return false;
    if (typeof response === 'string') {
      const trimmed = response.trim().toLowerCase();
      // Filter out common N/A patterns
      if (trimmed === '' || trimmed === 'n/a' || trimmed === 'na' || 
          trimmed === 'not applicable' || trimmed === 'no response' ||
          trimmed === 'prefer not to answer' || trimmed === 'skip') {
        return false;
      }
      // Try to parse as number
      const parsed = parseFloat(response);
      return !isNaN(parsed) && isFinite(parsed);
    }
    if (typeof response === 'number') {
      // Filter out common N/A numeric codes
      if (response === -1 || response === 999 || response === -999 || 
          !isFinite(response) || isNaN(response)) {
        return false;
      }
      return true;
    }
    return false;
  };

  // Extract numeric values from survey responses
  const extractValues = (responses, questionId) => {
    if (!responses || !Array.isArray(responses)) return [];
    
    return responses
      .map(response => {
        if (response && response.responses && Array.isArray(response.responses)) {
          const questionResponse = response.responses.find(r => r.question_id === questionId);
          if (questionResponse && isValidResponse(questionResponse.response)) {
            return typeof questionResponse.response === 'string' 
              ? parseFloat(questionResponse.response) 
              : questionResponse.response;
          }
        }
        return null;
      })
      .filter(val => val !== null);
  };

  // Calculate correlation matrix
  // 
  // N/A HANDLING STRATEGY:
  // - Individual N/A responses are filtered out when calculating averages for each respondent
  // - A respondent is completely excluded from correlation if they don't meet minimum thresholds:
  //   * Factor: Must have valid responses to at least 50% of factor questions
  //   * Outcome: Must have valid responses to at least 50% of outcome questions
  // - Only respondents with BOTH valid factor AND outcome averages are included
  // - This ensures perfectly aligned factor and outcome score arrays for correlation
  //
  const calculateCorrelationMatrix = useMemo(() => {
    if (!data || !core_data || !outcomeQ || 
        !core_data.questions || !core_data.questions.dimensions || 
        !outcomeQ.questions) return null;

    const factors = [];
    const outcomes = [];
    
    // Collect all factors (following calculations.js pattern)
    core_data.questions.dimensions.forEach((dimension, dimIndex) => {
      if (dimension && dimension.factors && Array.isArray(dimension.factors)) {
        dimension.factors.forEach((factor, factorIndex) => {
          if (factor && factor.title && factor.questions && Array.isArray(factor.questions)) {
            factors.push({
              title: factor.title,
              dimension: dimIndex,
              factorIndex: factorIndex,
              questionIndices: factor.questions.map((q, qIndex) => qIndex), // question indices in factor
              questions: factor.questions // keep original questions for reverse scoring
            });
          }
        });
      }
    });

    // Collect all outcome sections (aggregates, not individual questions)
    if (outcomeQ && outcomeQ.questions) {
      outcomeQ.questions.forEach(section => {
        if (section && section.name && section.id) {
          outcomes.push({
            id: section.id,
            name: section.name,
            section: section
          });
        }
      });
    }

    const matrix = [];

    factors.forEach(factor => {
      const factorRow = {
        factor: factor.title,
        dimension: factor.dimension,
        correlations: []
      };

             outcomes.forEach(outcome => {
         // Arrays to store valid factor and outcome scores (perfectly aligned)
         const factorScores = [];
         const outcomeScores = [];
         
         data.forEach(response => {
           // STEP 1: Calculate factor score for this respondent
           const factorValues = [];
           
           // Process culture factor questions (from response.questions)
           if (response.questions && Array.isArray(response.questions)) {
             for (const question of response.questions) {
               // Check if question belongs to this factor
               if (
                 question.id == factor.dimension && // dimension ID
                 question.factor == factor.factorIndex && // factor index
                 factor.questionIndices && factor.questionIndices.includes(question.q) // question index
               ) {
                 let resp = question.response;
                 if (isValidResponse(resp)) {
                   // Convert string to number if needed
                   if (typeof resp === 'string') {
                     resp = parseFloat(resp);
                   }
                   
                   // Apply reverse scoring if needed
                   const qIndex = question.q;
                   const questionObj = factor.questions[qIndex];
                   if (questionObj && questionObj.reverse) {
                     resp = 10 - resp;
                   }
                   
                   factorValues.push(resp);
                 }
               }
             }
           }
           
           // STEP 2: Calculate outcome score for this respondent
           const outcomeValues = [];
           
           if (response.employee_outcomes && response.employee_outcomes.responses) {
             // Get all responses for this outcome section
             outcome.section.questions.forEach(q => {
               const outcomeResponse = response.employee_outcomes.responses.find(r => {
                 return normalise(r.question) === normalise(q.question);
               });
               
               if (outcomeResponse && isValidResponse(outcomeResponse.response)) {
                 const val = typeof outcomeResponse.response === 'string' 
                   ? parseFloat(outcomeResponse.response) 
                   : outcomeResponse.response;
                 outcomeValues.push(val);
               }
             });
           }
           
           // STEP 3: Only include this respondent if BOTH factor and outcome meet thresholds
           const minFactorQuestions = Math.ceil((factor.questionIndices?.length || 1) * 0.5);
           const minOutcomeQuestions = Math.ceil((outcome.section.questions?.length || 1) * 0.5);
           
           const hasValidFactor = factorValues.length >= minFactorQuestions;
           const hasValidOutcome = outcomeValues.length >= minOutcomeQuestions;
           
           if (hasValidFactor && hasValidOutcome) {
             // Calculate averages
             const avgFactorScore = factorValues.reduce((a, b) => a + b, 0) / factorValues.length;
             const avgOutcomeScore = outcomeValues.reduce((a, b) => a + b, 0) / outcomeValues.length;
             
             // Add to aligned arrays
             factorScores.push(avgFactorScore);
             outcomeScores.push(avgOutcomeScore);
           }
           // If either factor or outcome is invalid, exclude this respondent entirely
         });

         // Calculate correlation if we have enough data points
         // Arrays are guaranteed to be the same length and properly aligned
         const correlation = factorScores.length >= 3
           ? calculateCorrelation(factorScores, outcomeScores)
           : 0;

         console.log(`Correlation: ${factor.title} vs ${outcome.name}`, {
           factorScores: factorScores.slice(0, 5), // Show first 5 for debugging
           outcomeScores: outcomeScores.slice(0, 5),
           totalDataPoints: factorScores.length,
           correlation: correlation
         });

         factorRow.correlations.push({
           outcome: outcome.name,
           value: correlation,
           dataPoints: factorScores.length
         });
       });

      matrix.push(factorRow);
    });

    console.log('Correlations - collected factors:', factors);
    console.log('Correlations - collected outcomes:', outcomes);
    console.log('Correlations - correlation matrix:', matrix);

    // Return null if no factors or outcomes found
    if (factors.length === 0 || outcomes.length === 0) {
      console.log('Correlations - no factors or outcomes found');
      return null;
    }

    // Calculate average correlations for sorting
    const factorAverages = matrix.map(row => {
      const validCorrelations = row.correlations
        .map(c => c.value)
        .filter(val => !isNaN(val) && isFinite(val));
      const average = validCorrelations.length > 0 
        ? validCorrelations.reduce((a, b) => a + b, 0) / validCorrelations.length 
        : 0;
      return {
        ...row,
        averageCorrelation: Math.abs(average) // Use absolute value for sorting
      };
    });

    const outcomeAverages = outcomes.map((outcome, outcomeIndex) => {
      const outcomeCorrelations = matrix
        .map(row => row.correlations[outcomeIndex]?.value || 0)
        .filter(val => !isNaN(val) && isFinite(val));
      const average = outcomeCorrelations.length > 0
        ? outcomeCorrelations.reduce((a, b) => a + b, 0) / outcomeCorrelations.length
        : 0;
      return {
        ...outcome,
        averageCorrelation: Math.abs(average) // Use absolute value for sorting
      };
    });

    // Sort by highest absolute average correlation
    const sortedFactors = factorAverages.sort((a, b) => b.averageCorrelation - a.averageCorrelation);
    const sortedOutcomes = outcomeAverages.sort((a, b) => b.averageCorrelation - a.averageCorrelation);

    // Rebuild matrix in sorted order with proper correlation mapping
    const sortedMatrix = sortedFactors.map(sortedFactor => {
      const originalRow = matrix.find(row => row.factor === sortedFactor.factor);
      const sortedCorrelations = sortedOutcomes.map(sortedOutcome => {
        return originalRow.correlations.find(corr => corr.outcome === sortedOutcome.name);
      });

      return {
        ...originalRow,
        correlations: sortedCorrelations,
        averageCorrelation: sortedFactor.averageCorrelation
      };
    });

    // Calculate final averages for display (using actual correlation values, not absolute)
    const finalFactorAverages = sortedMatrix.map(row => {
      const validCorrelations = row.correlations
        .map(c => c.value)
        .filter(val => !isNaN(val) && isFinite(val));
      return validCorrelations.length > 0 
        ? validCorrelations.reduce((a, b) => a + b, 0) / validCorrelations.length 
        : 0;
    });

    const finalOutcomeAverages = sortedOutcomes.map((outcome, outcomeIndex) => {
      const outcomeCorrelations = sortedMatrix
        .map(row => row.correlations[outcomeIndex]?.value || 0)
        .filter(val => !isNaN(val) && isFinite(val));
      return outcomeCorrelations.length > 0
        ? outcomeCorrelations.reduce((a, b) => a + b, 0) / outcomeCorrelations.length
        : 0;
    });

    return {
      factors: sortedFactors.map(f => ({ name: f.factor, factor: f.factor })), // Row labels (factors)
      outcomes: sortedOutcomes, // Column headers (outcomes)
      matrix: sortedMatrix,
      factorAverages: finalFactorAverages,
      outcomeAverages: finalOutcomeAverages
    };
  }, [data, core_data, outcomeQ]);

  useEffect(() => {
    console.log('Correlations - received data:', data);
    console.log('Correlations - core_data:', core_data);
    console.log('Correlations - outcomeQ:', outcomeQ);

    if (!data || data.length === 0) {
      console.log('Correlations - no data available');
      setIsEmpty(true);
      return;
    }

    setIsEmpty(false);
  }, [data]);

  useEffect(() => {
    setCorrelationMatrix(calculateCorrelationMatrix);
  }, [calculateCorrelationMatrix]);

  // Get color for correlation value
  const getCorrelationColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.7) return correlation > 0 ? '#1a5c1a' : '#8b0000';
    if (abs >= 0.5) return correlation > 0 ? '#2d8f2d' : '#cd5c5c';
    if (abs >= 0.3) return correlation > 0 ? '#4caf50' : '#f08080';
    if (abs >= 0.1) return correlation > 0 ? '#81c784' : '#ffa07a';
    return '#f5f5f5';
  };

  const getTextColor = (correlation) => {
    const abs = Math.abs(correlation);
    return abs >= 0.3 ? '#ffffff' : '#2A3039';
  };

  if (!core_data) {
    return <div>Loading...</div>;
  }

  return (
    <Container>
      <BlueTitle>Culture Analytics</BlueTitle>
      <Title>Correlation Analysis</Title>
      <Description>
        This heatmap shows the correlation between culture factors and overall business outcome scores. 
        Each outcome represents the aggregate score across all questions in that outcome category.
        Factors and outcomes are sorted by their highest average absolute correlations.
        Average correlations are displayed on the right (factor averages) and bottom (outcome averages).
        Darker colors indicate stronger correlations (positive in green, negative in red).
        N/A responses are excluded, and correlations require at least 3 matched data points.
        Cells marked with * have low sample sizes ({'<'}10) and should be interpreted with caution.
      </Description>

      {(!isEmpty && correlationMatrix) ? (
        <Content>
          <HeatmapContainer>
            <HeatmapTable>
              <thead>
                <tr>
                  <HeaderCell>Factor</HeaderCell>
                  {correlationMatrix.outcomes.map(outcome => (
                    <HeaderCell key={outcome.name}>{outcome.name}</HeaderCell>
                  ))}
                  <AverageHeaderCell>Average</AverageHeaderCell>
                </tr>
              </thead>
              <tbody>
                {correlationMatrix.matrix.map((row, index) => (
                  <tr key={index}>
                    <FactorCell>{row.factor}</FactorCell>
                    {row.correlations.map((corr, corrIndex) => (
                      <CorrelationCell
                        key={corrIndex}
                        backgroundColor={getCorrelationColor(corr.value)}
                        textColor={getTextColor(corr.value)}
                        title={`Correlation: ${corr.value.toFixed(3)}\nData points: ${corr.dataPoints}${corr.dataPoints < 10 ? '\n(Low sample size - interpret with caution)' : ''}`}
                        isLowConfidence={corr.dataPoints < 10}
                      >
                        {corr.dataPoints >= 3 ? corr.value.toFixed(2) : 'N/A'}
                        {corr.dataPoints < 10 && corr.dataPoints >= 3 && (
                          <LowConfidenceIndicator>*</LowConfidenceIndicator>
                        )}
                      </CorrelationCell>
                    ))}
                    <AverageCell
                      title={`Factor Average: ${correlationMatrix.factorAverages[index].toFixed(3)}`}
                    >
                      {correlationMatrix.factorAverages[index].toFixed(2)}
                    </AverageCell>
                  </tr>
                ))}
                <tr>
                  <AverageFactorCell>Average</AverageFactorCell>
                  {correlationMatrix.outcomeAverages.map((avg, avgIndex) => (
                    <AverageCell
                      key={avgIndex}
                      title={`Outcome Average: ${avg.toFixed(3)}`}
                    >
                      {avg.toFixed(2)}
                    </AverageCell>
                  ))}
                  <OverallAverageCell>
                    {(() => {
                      const allValidCorrelations = correlationMatrix.matrix
                        .flatMap(row => row.correlations)
                        .map(c => c.value)
                        .filter(val => !isNaN(val) && isFinite(val) && val !== 0);
                      const overallAvg = allValidCorrelations.length > 0
                        ? allValidCorrelations.reduce((a, b) => a + b, 0) / allValidCorrelations.length
                        : 0;
                      return overallAvg.toFixed(2);
                    })()}
                  </OverallAverageCell>
                </tr>
              </tbody>
            </HeatmapTable>
          </HeatmapContainer>
          
          <Legend>
            <LegendTitle>Correlation Strength</LegendTitle>
            <LegendRow>
              <LegendItem>
                <LegendColor bgColor="#1a5c1a" />
                <span>Strong Positive (≥0.7)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#2d8f2d" />
                <span>Moderate Positive (0.5-0.7)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#4caf50" />
                <span>Weak Positive (0.3-0.5)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#f5f5f5" />
                <span>Negligible (0-0.3)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#ffa07a" />
                <span>Weak Negative (-0.3-0)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#f08080" />
                <span>Moderate Negative (-0.5--0.3)</span>
              </LegendItem>
              <LegendItem>
                <LegendColor bgColor="#8b0000" />
                <span>Strong Negative (≤-0.5)</span>
              </LegendItem>
            </LegendRow>
          </Legend>
        </Content>
      ) : (
        <Empty addMargin={false} />
      )}
    </Container>
  );
};

export default CorrelationAnalytics;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: 'Raleway';
`;

const BlueTitle = styled.div`
  color: #2D70E2;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
  margin-top: -20px;
`;

const Title = styled.div`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 30px;
  color: #2A3039;
  display: flex;
`;

const Description = styled.div`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 30px;
  line-height: 1.5;
  color: #2A3039;
  max-width: 800px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin-top: 20px;
`;

const HeatmapContainer = styled.div`
  overflow-x: auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const HeatmapTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const HeaderCell = styled.th`
  background: #f8f9fa;
  color: #2A3039;
  font-weight: 600;
  font-size: 12px;
  padding: 12px 8px;
  text-align: center;
  border-bottom: 2px solid #ddd;
  border-right: 1px solid #ddd;
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:first-child {
    text-align: left;
    min-width: 150px;
  }
`;

const AverageHeaderCell = styled.th`
  background: #f8f9fa;
  color: #2A3039;
  font-weight: 600;
  font-size: 12px;
  padding: 12px 8px;
  text-align: center;
  border-bottom: 2px solid #ddd;
  border-right: 1px solid #ddd;
  position: sticky;
  top: 0;
  z-index: 10;
  min-width: 100px; /* Adjust as needed */
`;

const FactorCell = styled.td`
  background: #f8f9fa;
  color: #2A3039;
  font-weight: 500;
  font-size: 11px;
  padding: 12px 8px;
  border-right: 2px solid #ddd;
  border-bottom: 1px solid #ddd;
  min-width: 150px;
`;

const AverageFactorCell = styled.td`
  background: #f8f9fa;
  color: #2A3039;
  font-weight: 500;
  font-size: 11px;
  padding: 12px 8px;
  border-right: 2px solid #ddd;
  border-bottom: 1px solid #ddd;
  min-width: 150px;
`;

const CorrelationCell = styled.td`
  background-color: ${props => props.backgroundColor};
  color: ${props => props.textColor};
  font-weight: 600;
  font-size: 11px;
  padding: 12px 8px;
  text-align: center;
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  position: relative;
  ${props => props.isLowConfidence && `
    border: 2px dashed #ff9800;
    opacity: 0.8;
  `}
`;

const AverageCell = styled.td`
  background-color: #f8f9fa;
  color: #2A3039;
  font-weight: 600;
  font-size: 11px;
  padding: 12px 8px;
  text-align: center;
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  position: relative;
`;

const OverallAverageCell = styled.td`
  background-color: #f8f9fa;
  color: #2A3039;
  font-weight: 600;
  font-size: 11px;
  padding: 12px 8px;
  text-align: center;
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  position: relative;
`;

const LowConfidenceIndicator = styled.span`
  color: #ff9800;
  font-weight: bold;
  margin-left: 2px;
  font-size: 14px;
`;

const Legend = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const LegendTitle = styled.div`
  font-weight: 600;
  color: #2A3039;
  margin-bottom: 15px;
  font-size: 16px;
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #2A3039;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${props => props.bgColor};
  border-radius: 3px;
  border: 1px solid #ddd;
`;
